import { useEffect, useRef, useState } from "react";
import { API } from "../lib/api";
import { containerStyle, textInnerStyle, enterAnimCss, exitAnimCss, resolveAnim } from "../lib/elementStyle";
import { TimerText } from "./TimerText";
import { DynamicElement } from "./DynamicElement";
import { ChatFeed, PinnedComment, SuperChatAlert } from "./ChatElements";
import { SheetText, SheetImage } from "./SheetText";
import { ShapeElement } from "./ShapeElement";
import { groupAnimOf, groupBBox } from "../lib/groups";

export function OverlayElement({ el, media, triggerKey, ytData, twitchData, chatState, sheetValues }) {
  const visible = el.visivel !== false;
  const [phase, setPhase] = useState(visible ? "shown" : "hidden");
  const [inKey, setInKey] = useState(0);
  const prevVis = useRef(visible);

  // Play exit animation on hide, entrance on show.
  useEffect(() => {
    if (visible && !prevVis.current) {
      setPhase("shown");
      setInKey((k) => k + 1);
    } else if (!visible && prevVis.current) {
      const ex = exitAnimCss(el);
      if (ex) {
        setPhase("exiting");
        const dur = resolveAnim(el.animacao).saida.duracao || 400;
        prevVis.current = visible;
        const t = setTimeout(() => setPhase("hidden"), dur);
        return () => clearTimeout(t);
      }
      setPhase("hidden");
    }
    prevVis.current = visible;
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-trigger entrance when operator fires the "disparar" command.
  useEffect(() => {
    if (visible) {
      setPhase("shown");
      setInKey((k) => k + 1);
    }
  }, [triggerKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === "hidden") return null;

  const anim = phase === "exiting" ? exitAnimCss(el) : enterAnimCss(el);
  // Camada externa: posição + animação. Camada interna: rotação e opacidade da camada
  // (assim a animação não apaga a rotação/opacidade definidas no editor).
  const { transform: rot, opacity: op, ...box } = containerStyle(el);
  const style = { ...box, ...(anim || {}) };
  const wrapKey = phase === "exiting" ? "out" : `in-${inKey}`;

  let content;
  const bound = el.sheetBind?.enabled;
  const sv = sheetValues?.[el.id];
  if (bound) {
    const first = Array.isArray(sv) ? sv[0] : sv;
    const looksImg = first && /^https?:\/\//i.test(String(first));
    content = (el.tipo === "media" || looksImg) ? <SheetImage el={el} values={sv} /> : <SheetText el={el} values={sv} />;
  } else if (el.tipo === "media") {
    const m = media?.[el.mediaId];
    if (el.mediaId) {
      const src = `${API}/media/${el.mediaId}/file`;
      const isVideo = m ? m.kind === "video" : true;
      content = isVideo ? (
        <video key={`v-${el.autoplay !== false}-${el.loop !== false}`} src={src}
          autoPlay={el.autoplay !== false || inKey > 0} muted loop={el.loop !== false} playsInline
          style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      ) : (
        <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      );
    }
  } else if (el.tipo === "dynamic") {
    content = <DynamicElement el={el} ytData={ytData} twitchData={twitchData} />;
  } else if (el.tipo === "chat") {
    content = <ChatFeed el={el} messages={chatState?.messages} />;
  } else if (el.tipo === "comment") {
    content = <PinnedComment el={el} pinned={chatState?.pinned} />;
  } else if (el.tipo === "superchat") {
    content = <SuperChatAlert el={el} alert={chatState?.alert} />;
  } else if (el.tipo === "forma") {
    content = <ShapeElement el={el} />;
  } else if (el.papel === "cronometro") {
    content = <TimerText timer={el.timer} style={textInnerStyle(el.estilo || {})} />;
  } else {
    content = <div style={textInnerStyle(el.estilo || {})}>{el.conteudo}</div>;
  }

  return (
    <div key={wrapKey} style={style} data-testid={`overlay-el-${el.id}`}>
      <div style={{ width: "100%", height: "100%", transform: rot, opacity: op }}>
        {content}
      </div>
    </div>
  );
}

// Grupo com animação própria (entrada/saída do grupo inteiro, independente das camadas).
// Ligar/desligar o grupo toca a animação do grupo e, ao mesmo tempo, a de cada camada.
export function GroupAnimated({ members, renderMember }) {
  const anim = groupAnimOf(members);
  const fake = { animacao: anim };
  const visible = members.some((m) => m.visivel !== false);
  const [phase, setPhase] = useState(visible ? "shown" : "hidden");
  const [inKey, setInKey] = useState(0);
  const prevVis = useRef(visible);
  const frozen = useRef(members);
  if (visible) frozen.current = members;

  useEffect(() => {
    if (visible && !prevVis.current) {
      setPhase("shown");
      setInKey((k) => k + 1);
    } else if (!visible && prevVis.current) {
      prevVis.current = visible;
      if (exitAnimCss(fake)) {
        setPhase("exiting");
        const own = members.filter((m) => exitAnimCss(m)).map((m) => resolveAnim(m.animacao).saida.duracao || 400);
        const t = setTimeout(() => setPhase("hidden"), Math.max(resolveAnim(anim).saida.duracao || 400, ...own));
        return () => clearTimeout(t);
      }
      setPhase("hidden");
    }
    prevVis.current = visible;
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible && phase !== "exiting") return null;
  const bb = groupBBox(members);
  const css = phase === "exiting" ? exitAnimCss(fake) : enterAnimCss(fake);
  // Na saída: camadas com animação de saída própria tocam a sua junto com a do grupo; as que não
  // têm ficam paradas (visíveis) até o grupo terminar, para a animação do grupo aparecer.
  const list = phase === "exiting"
    ? members.map((m) => (exitAnimCss(m) ? m : (frozen.current.find((f) => f.id === m.id) || m)))
    : members;
  // Ordem visual: cada camada ganha o seu próprio invólucro com o z-index dela (em vez de um único invólucro
  // no z-index mais alto do grupo), então a pilha segue o menu Camadas mesmo com camadas soltas entre as do grupo.
  // Todos os invólucros tocam a mesma animação ao mesmo tempo (mesmo delay e mesma origem), e cada camada
  // ainda toca a animação própria dentro dele.
  return (
    <>
      {[...list].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((el) => (
        <div
          key={`${el.id}-${phase === "exiting" ? "g-out" : `g-in-${inKey}`}`}
          style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            zIndex: el.zIndex || 0,
            transformOrigin: `${bb.x + bb.w / 2}px ${bb.y + bb.h / 2}px`,
            ...(css || {}),
          }}
          data-testid="overlay-group"
        >
          {renderMember(el)}
        </div>
      ))}
    </>
  );
}
